import React, { useEffect, useState } from 'react';
import { Checkbox, Modal } from '@carbon/react';
import axios from 'axios';
import moment from 'moment';

import { GatewayIp } from '../../../data/endpoints';
import { ISchedulerProfile } from '../../../AppSlice';
import RecorderAdvancedConfigForm, {
  AdvancedConfig,
  AdvancedScheduleConfig,
  FilesystemResource,
  RtmpResource,
  SdiResource,
  parseAdvancedConfig,
  buildAdvancedConfigParams,
  emptyAdvancedConfig,
  emptyAdvancedScheduleConfig,
} from './RecorderAdvancedConfigForm';

interface SlotResult {
  slotId: string;
  slotLabel: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
}

interface SlotDefaultConfig {
  id: string;
  taskType: string;
  params: Record<string, any>;
  validTo?: string | null;
  tz?: string;
  enabled?: boolean;
  overlapPolicy?: string;
  misfirePolicy?: string;
  retryPolicy?: Record<string, any>;
}

interface Props {
  open: boolean;
  slotIds: string[];
  slotLabelById: Record<string, string>;
  token: string | null;
  initialStartAt?: string;
  initialEndAt?: string;
  mediaengineProfiles?: ISchedulerProfile[];
  sdiResources?: SdiResource[];
  filesystemResources?: FilesystemResource[];
  rtmpResources?: RtmpResource[];
  onClose: () => void;
  onScheduleCreated: () => void;
}

const SCHEDULES_ENDPOINT = `${GatewayIp}/api/scheduler/schedules`;
const slotBase = (slotId: string) => `${GatewayIp}/api/scheduler/slots/${slotId}`;

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const dtInputStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#262626',
  border: '1px solid #525252',
  color: '#fff',
  padding: '9px 12px',
  borderRadius: 4,
  fontSize: 14,
  boxSizing: 'border-box',
  colorScheme: 'dark',
};

const SlotScheduleModal: React.FC<Props> = ({
  open,
  slotIds,
  slotLabelById,
  token,
  initialStartAt,
  initialEndAt,
  mediaengineProfiles = [],
  sdiResources = [],
  filesystemResources = [],
  rtmpResources = [],
  onClose,
  onScheduleCreated,
}) => {
  const [startAt, setStartAt] = useState(initialStartAt ?? '');
  const [endAt, setEndAt] = useState(initialEndAt ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<SlotResult[] | null>(null);
  const [advancedEnabled, setAdvancedEnabled] = useState(false);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [slotOverrides, setSlotOverrides] = useState<Record<string, AdvancedConfig>>({});
  const [slotScheduleOverrides, setSlotScheduleOverrides] = useState<Record<string, AdvancedScheduleConfig>>({});
  const [slotRawConfigs, setSlotRawConfigs] = useState<Record<string, SlotDefaultConfig>>({});
  const [activeOverrideSlotId, setActiveOverrideSlotId] = useState('');
  const [slotsWithNoConfig, setSlotsWithNoConfig] = useState<Set<string>>(new Set());

  const loadOverrides = async (currentToken: string, currentSlotIds: string[]) => {
    if (!currentToken || currentSlotIds.length === 0) return;
    setLoadingOverrides(true);
    setOverrideError(null);

    const settled = await Promise.allSettled(
      currentSlotIds.map(async (slotId) => {
        const response = await axios.get(
          `${slotBase(slotId)}/default-configs`,
          { withCredentials: true, headers: authHeaders(currentToken) }
        );
        const items: SlotDefaultConfig[] = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.items) ? response.data.items : [];
        const activeConfig = items.find((c) => c.validTo === null) ?? items[0] ?? null;
        return { slotId, config: activeConfig };
      })
    );

    const nextOverrides: Record<string, AdvancedConfig> = {};
    const nextScheduleOverrides: Record<string, AdvancedScheduleConfig> = {};
    const nextRawConfigs: Record<string, SlotDefaultConfig> = {};
    const nextSlotsWithNoConfig = new Set<string>();
    let hasError = false;

    settled.forEach((result, index) => {
      const slotId = currentSlotIds[index];
      if (result.status === 'fulfilled') {
        if (result.value.config) {
          const cfg = result.value.config;
          nextOverrides[slotId] = parseAdvancedConfig(cfg.taskType, cfg.params);
          nextRawConfigs[slotId] = cfg;
          nextScheduleOverrides[slotId] = {
            enabled: cfg.enabled ?? true,
            tz: cfg.tz ?? 'UTC',
            overlapPolicy: cfg.overlapPolicy ?? 'SKIP_IF_RUNNING',
            misfirePolicy: cfg.misfirePolicy ?? 'START_IF_BEFORE_END',
            retryMaxAttempts: Number(cfg.retryPolicy?.maxAttempts ?? 3),
            retryInitialMs: Number(cfg.retryPolicy?.initialMs ?? 1000),
            retryBackoff: cfg.retryPolicy?.backoff ?? 'exponential',
          };
        } else {
          nextSlotsWithNoConfig.add(slotId);
          nextOverrides[slotId] = emptyAdvancedConfig();
          nextScheduleOverrides[slotId] = emptyAdvancedScheduleConfig();
          nextRawConfigs[slotId] = { id: '', taskType: 'MEDIAENGINE_RECORD', params: {} };
        }
      } else {
        hasError = true;
        nextOverrides[slotId] = emptyAdvancedConfig();
        nextScheduleOverrides[slotId] = emptyAdvancedScheduleConfig();
        nextRawConfigs[slotId] = { id: '', taskType: 'MEDIAENGINE_RECORD', params: {} };
      }
    });

    setSlotOverrides(nextOverrides);
    setSlotScheduleOverrides(nextScheduleOverrides);
    setSlotRawConfigs(nextRawConfigs);
    setSlotsWithNoConfig(nextSlotsWithNoConfig);
    if (nextSlotsWithNoConfig.size > 0) {
      setAdvancedEnabled(true);
    }
    setLoadingOverrides(false);

    if (hasError) {
      setOverrideError('Some slot default configs could not be loaded. Fallback values are shown.');
    }
  };

  useEffect(() => {
    if (!open) return;
    setStartAt(initialStartAt ?? '');
    setEndAt(initialEndAt ?? '');
    setSubmitting(false);
    setResults(null);
    setAdvancedEnabled(false);
    setLoadingOverrides(false);
    setOverrideError(null);
    setSlotOverrides({});
    setSlotScheduleOverrides({});
    setSlotRawConfigs({});
    setSlotsWithNoConfig(new Set());
    setActiveOverrideSlotId(slotIds[0] ?? '');
  }, [open, initialStartAt, initialEndAt]);

  const slotIdsKey = slotIds.join(',');
  useEffect(() => {
    if (!open || !token) return;
    void loadOverrides(token, slotIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slotIdsKey, token]);

  const advancedRequired = slotsWithNoConfig.size > 0;
  const isValid = Boolean(startAt && endAt);
  const hasResults = Boolean(results);
  const anySuccess = results?.some((r) => r.status === 'success');
  const activeOverride = activeOverrideSlotId ? slotOverrides[activeOverrideSlotId] : null;
  const activeScheduleOverride = activeOverrideSlotId ? slotScheduleOverrides[activeOverrideSlotId] : null;

  const updateActiveOverride = (updates: Partial<AdvancedConfig>) => {
    if (!activeOverrideSlotId) return;
    setSlotOverrides((current) => ({
      ...current,
      [activeOverrideSlotId]: { ...current[activeOverrideSlotId], ...updates },
    }));
  };

  const updateActiveScheduleOverride = (updates: Partial<AdvancedScheduleConfig>) => {
    if (!activeOverrideSlotId) return;
    setSlotScheduleOverrides((current) => ({
      ...current,
      [activeOverrideSlotId]: { ...current[activeOverrideSlotId], ...updates },
    }));
  };

  const handleSubmit = async () => {
    if (!token || !slotIds.length) return;
    setSubmitting(true);

    const initial: SlotResult[] = slotIds.map((id) => ({
      slotId: id,
      slotLabel: slotLabelById[id] ?? id,
      status: 'pending',
    }));
    setResults(initial);

    const updated = [...initial];
    const startAtIso = moment(startAt).utc().toISOString();
    const endAtIso = moment(endAt).utc().toISOString();

    await Promise.allSettled(
      slotIds.map(async (slotId, index) => {
        try {
          const rawConfig = slotRawConfigs[slotId];
          const override = slotOverrides[slotId];
          const scheduleOverride = slotScheduleOverrides[slotId];

          const taskType = advancedEnabled && override
            ? override.taskType
            : rawConfig?.taskType ?? 'MEDIAENGINE_RECORD';

          const params = advancedEnabled && override
            ? buildAdvancedConfigParams(override)
            : rawConfig?.params ?? {};

          const payload = {
            slotId,
            label: `${slotLabelById[slotId] ?? slotId} - ${moment(startAt).format('YYYY-MM-DD HH:mm')}`,
            taskType,
            params,
            startAt: startAtIso,
            endAt: endAtIso,
            tz: advancedEnabled ? (scheduleOverride?.tz ?? rawConfig?.tz ?? 'UTC') : (rawConfig?.tz ?? 'UTC'),
            enabled: advancedEnabled ? (scheduleOverride?.enabled ?? true) : true,
            overlapPolicy: advancedEnabled ? (scheduleOverride?.overlapPolicy ?? rawConfig?.overlapPolicy ?? 'SKIP_IF_RUNNING') : (rawConfig?.overlapPolicy ?? 'SKIP_IF_RUNNING'),
            misfirePolicy: advancedEnabled ? (scheduleOverride?.misfirePolicy ?? rawConfig?.misfirePolicy ?? 'START_IF_BEFORE_END') : (rawConfig?.misfirePolicy ?? 'START_IF_BEFORE_END'),
            retryPolicy: advancedEnabled ? {
              maxAttempts: Number(scheduleOverride?.retryMaxAttempts ?? rawConfig?.retryPolicy?.maxAttempts ?? 3),
              initialMs: Number(scheduleOverride?.retryInitialMs ?? rawConfig?.retryPolicy?.initialMs ?? 1000),
              backoff: scheduleOverride?.retryBackoff ?? rawConfig?.retryPolicy?.backoff ?? 'exponential',
            } : rawConfig?.retryPolicy ?? {
              backoff: 'exponential',
              initialMs: 1000,
              maxAttempts: 3,
            },
          };

          await axios.post(SCHEDULES_ENDPOINT, payload, {
            withCredentials: true,
            headers: authHeaders(token),
          });

          updated[index] = { ...updated[index], status: 'success' };
        } catch (err: any) {
          updated[index] = {
            ...updated[index],
            status: 'error',
            message: err?.response?.data?.message ?? 'Failed',
          };
        }
        setResults([...updated]);
      })
    );

    setSubmitting(false);
    if (updated.every((r) => r.status === 'success')) {
      onScheduleCreated();
    }
  };

  const handleClose = () => {
    if (anySuccess) onScheduleCreated();
    onClose();
  };

  return (
    <Modal
      open={open}
      size="lg"
      modalHeading="Schedule"
      primaryButtonText={hasResults ? 'Done' : 'Create Schedule'}
      secondaryButtonText={hasResults ? undefined : 'Cancel'}
      primaryButtonDisabled={!hasResults && (!isValid || submitting || loadingOverrides)}
      onRequestClose={handleClose}
      onRequestSubmit={hasResults ? handleClose : handleSubmit}
      onSecondarySubmit={onClose}
      preventCloseOnClickOutside
    >
      <p style={{ color: '#8d8d8d', fontSize: 13, marginBottom: 16 }}>
        Applies to <strong style={{ color: '#ccc' }}>{slotIds.length}</strong> selected slot{slotIds.length !== 1 ? 's' : ''}
      </p>

      {!hasResults && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 6, color: '#ccc' }}>Start At</label>
            <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} style={dtInputStyle} />
            <div style={{ fontSize: 11, color: '#8d8d8d', marginTop: 4 }}>Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 6, color: '#ccc' }}>End At</label>
            <input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} style={dtInputStyle} />
            <div style={{ fontSize: 11, color: '#8d8d8d', marginTop: 4 }}>Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</div>
          </div>

          <div>
            <Checkbox
              id="schedule-advanced-configs"
              labelText="Advanced configurations"
              checked={advancedEnabled || advancedRequired}
              disabled={advancedRequired}
              onChange={(_, { checked }) => { if (!advancedRequired) setAdvancedEnabled(Boolean(checked)); }}
            />
            {advancedRequired && (
              <div style={{ fontSize: 12, color: '#f1c21b', marginTop: 4 }}>
                Required — {slotsWithNoConfig.size === 1 ? 'one selected slot has' : `${slotsWithNoConfig.size} selected slots have`} no active default configuration.
              </div>
            )}
          </div>

          {advancedEnabled && (
            <div style={{ backgroundColor: '#1e1e1e', border: '1px solid #393939', borderRadius: 6, padding: 16 }}>
              {loadingOverrides ? (
                <div style={{ color: '#8d8d8d' }}>Loading slot configurations…</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {overrideError && (
                    <div style={{ color: '#f1c21b', fontSize: 12 }}>{overrideError}</div>
                  )}

                  <div>
                    <div style={{ fontSize: 12, color: '#8d8d8d', marginBottom: 8 }}>Selected Slots</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {slotIds.map((slotId) => (
                        <button
                          key={slotId}
                          type="button"
                          onClick={() => setActiveOverrideSlotId(slotId)}
                          style={{
                            backgroundColor: activeOverrideSlotId === slotId ? '#0f62fe' : '#262626',
                            color: '#fff',
                            border: '1px solid #525252',
                            borderRadius: 6,
                            padding: '6px 10px',
                            cursor: 'pointer',
                          }}
                        >
                          {slotLabelById[slotId] ?? slotId}
                        </button>
                      ))}
                    </div>
                  </div>

                  {activeOverride && (
                    <RecorderAdvancedConfigForm
                      value={activeOverride}
                      onChange={updateActiveOverride}
                      sdiResources={sdiResources}
                      filesystemResources={filesystemResources}
                      rtmpResources={rtmpResources}
                      mediaengineProfiles={mediaengineProfiles}
                      scheduleConfig={activeScheduleOverride ?? emptyAdvancedScheduleConfig()}
                      onScheduleConfigChange={updateActiveScheduleOverride}
                      idPrefix={`schedule-override-${activeOverrideSlotId}`}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {hasResults && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {results.map((r) => (
            <div
              key={r.slotId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 4,
                backgroundColor: '#1e1e1e',
                border: `1px solid ${r.status === 'success' ? '#24a148' : r.status === 'error' ? '#da1e28' : '#525252'}`,
              }}
            >
              <span style={{ fontSize: 16 }}>
                {r.status === 'success' ? '✓' : r.status === 'error' ? '✕' : '…'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#ccc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.slotLabel}
                </div>
                {r.status === 'error' && r.message && (
                  <div style={{ fontSize: 11, color: '#fa4d56', marginTop: 2 }}>{r.message}</div>
                )}
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: r.status === 'success' ? '#24a148' : r.status === 'error' ? '#da1e28' : '#8d8d8d',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                {r.status === 'pending' && submitting ? 'Running' : r.status}
              </span>
            </div>
          ))}
          {!submitting && anySuccess && (
            <p style={{ marginTop: 8, fontSize: 12, color: '#24a148' }}>
              Schedule{results.filter((r) => r.status === 'success').length > 1 ? 's' : ''} created — timeline will refresh.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
};

export default SlotScheduleModal;
